import { useState, useRef, useEffect, ReactNode } from "react";
import { IQuestionnaireCategory } from "../Interfaces/IQuestionnaireInterface";
import Categories from "./Categories/Categories";
import styles from "./Questionnaire.module.css";
import ProgressBar from "./ProgressBar/ProgressBar";
import Choice from "./TypesQuestions/Choice";
import { BlueButton } from "@react-solutions-ui/findmore-ui";
import { WhiteButton } from "@react-solutions-ui/findmore-ui";
import { QuestionnaireResponses } from "../pages/PublicQuestionnaire/PublicQuestionnaire";
import { Modal } from "@react-solutions-ui/findmore-ui";
import { useNavigate } from "react-router-dom";
import exclamationIcon from "../../../assets/icons/ExclamationCircle.svg";
import CloseIcon from "../../../assets/icons/closeIconBlack.svg";

export type ModalInfo = {
  type: "confirm" | "info";
  title: string;
  content: ReactNode;
  action: () => void;
  closeAction: () => void;
  confirmActionLabel: string;
  closeActionLabel: string;
  isSending?: boolean;
};

export default function Questionnaire({
  data,
  responses,
  onResponseChange,
  onSubmit,
  onSaveResponses,
  completedCategories,
  autoEvaluation,
  evaluatedName,
  savingResponses,
  lastSavedTime,
  autoCompleted,
  evaluatorType,
  evaluationId,
}: {
  data: IQuestionnaireCategory[];
  responses: QuestionnaireResponses;
  onResponseChange: (questionId: string, answer: string, type: string) => void;
  onSubmit: () => void;
  onSaveResponses: (questionId: string, answer: string) => void;
  completedCategories: string[];
  autoEvaluation: boolean;
  evaluatedName: string;
  savingResponses: boolean;
  lastSavedTime: Date | null;
  autoCompleted: boolean;
  evaluatorType: string;
  evaluationId: string;
}) {
  const [modalInfo, setModalInfo] = useState<ModalInfo | null>(null);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  const questionnaireKey = `warning_closed_${evaluationId}`;

  const [hideWarning, setHideWarning] = useState(() => {
    const stored = sessionStorage.getItem(questionnaireKey);
    return stored === "true";
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (hideWarning) {
      sessionStorage.setItem(questionnaireKey, "true");
    }
  }, [hideWarning, questionnaireKey]);

  const isResponseValid = (question: any) => {
    const val = responses?.[question.id];
    if (question.questionType === "Text") {
      return typeof val === "string" && val.trim().length > 0;
    }
    return val !== undefined && val !== null && val !== "";
  };

  const computedCompletedCategories = data
    .filter((category) =>
      category.questions.every((q: any) => isResponseValid(q))
    )
    .map((c) => c.categoryName);

  const allCategoriesAnswered =
    computedCompletedCategories.length === data.length;

  const [activeCategory, setActiveCategory] = useState<string>(() => {
    const firstIncompleteCategory = data.find(
      (category) => !computedCompletedCategories.includes(category.categoryName)
    );
    return firstIncompleteCategory?.categoryName || data[0].categoryName;
  });

  // Textarea
  const textareaRefs = useRef<{ [key: string]: HTMLTextAreaElement | null }>(
    {}
  );

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useEffect(() => {
    Object.values(textareaRefs.current).forEach((textarea) => {
      if (textarea) {
        adjustTextareaHeight(textarea);
      }
    });
  }, [activeCategory]);

  // Update current time when lastSavedTime changes and every minute to refresh relative time display
  useEffect(() => {
    if (!lastSavedTime) return;

    setCurrentTime(new Date());

    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, [lastSavedTime]);

  const getRelativeTime = (savedTime: Date): string => {
    const now = currentTime;
    const diffInSeconds = Math.floor(
      (now.getTime() - savedTime.getTime()) / 1000
    );

    if (diffInSeconds < 60) {
      return evaluatorType === "Client"
        ? "a few seconds ago"
        : "há poucos segundos";
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return evaluatorType === "Client"
        ? `${diffInMinutes} ${diffInMinutes === 1 ? "minute" : "minutes"} ago`
        : `há ${diffInMinutes} ${diffInMinutes === 1 ? "minuto" : "minutos"}`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return evaluatorType === "Client"
        ? `${diffInHours} ${diffInHours === 1 ? "hour" : "hours"} ago`
        : `há ${diffInHours} ${diffInHours === 1 ? "hora" : "horas"}`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    return evaluatorType === "Client"
      ? `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`
      : `há ${diffInDays} ${diffInDays === 1 ? "dia" : "dias"}`;
  };

  const formerCategory = () => {
    const currentIndex = data.findIndex(
      (category) => category.categoryName === activeCategory
    );
    if (currentIndex > 0) {
      setActiveCategory(data[currentIndex - 1].categoryName);
    } else {
      setActiveCategory(data[0].categoryName);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const nextCategory = () => {
    const currentIndex = data.findIndex(
      (category) => category.categoryName === activeCategory
    );
    if (currentIndex < data.length - 1) {
      setActiveCategory(data[currentIndex + 1].categoryName);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getProgressActiveCategory = () => {
    // Get all the ids of the questions of the active category
    const questionsIds =
      data
        .find((category) => category.categoryName === activeCategory)
        ?.questions.map((question) => question.id) || [];
    // Check how many questions are completed
    const completedQuestions = questionsIds.filter(
      (questionId) => responses[questionId]
    );
    return (completedQuestions.length / questionsIds.length) * 100;
  };

  const ModalEvaluationSubmition = (name: string) => {
    setModalInfo({
      type: "confirm",
      title: autoEvaluation
        ? evaluatorType === "Client"
          ? "Submit self-evaluation"
          : "Submeter autoavaliação"
        : evaluatorType === "Client"
        ? `Submit evaluation of ${name}`
        : `Submeter avaliação de ${name}`,
      content: (
        <>
          <p
            style={{
              marginBottom: "8px",
              marginTop: "0",
              marginLeft: "0",
              marginRight: "0",
            }}
          >
            {evaluatorType === "Client"
              ? "After submitting, you will not be able to make changes."
              : "Depois de submeter, já não poderás fazer alterações."}
          </p>
          <p style={{ margin: "0" }}>
            {evaluatorType === "Client"
              ? "Are you sure you want to continue?"
              : "Tens a certeza que queres continuar?"}
          </p>
        </>
      ),
      action: async () => {
        setModalInfo({
          type: "info",
          title: autoEvaluation
            ? evaluatorType === "Client"
              ? "Submit self-evaluation"
              : "Submeter autoavaliação"
            : evaluatorType === "Client"
            ? `Submit evaluation of ${name}`
            : evaluatorType === "Client"
            ? `Submit evaluation of ${name}`
            : `Submeter avaliação de ${name}`,
          content: (
            <>
              <p
                style={{
                  marginBottom: "8px",
                  marginTop: "0",
                  marginLeft: "0",
                  marginRight: "0",
                }}
              >
                {evaluatorType === "Client"
                  ? "After submitting, you will not be able to make changes."
                  : "Depois de submeter, já não poderás fazer alterações."}
              </p>
              <p style={{ margin: "0" }}>
                {evaluatorType === "Client"
                  ? "Are you sure you want to continue?"
                  : "Tens a certeza que queres continuar?"}
              </p>
            </>
          ),
          action: async () => {},
          closeAction: () => setModalInfo(null),
          confirmActionLabel:
            evaluatorType === "Client" ? "Yes, submit" : "Sim, submeter",
          closeActionLabel: evaluatorType === "Client" ? "Sending" : "A enviar",
          isSending: true,
        });
        await onSubmit();
        setModalInfo(null);
      },
      closeAction: () => setModalInfo(null),
      confirmActionLabel:
        evaluatorType === "Client" ? "Yes, submit" : "Sim, submeter",
      closeActionLabel: evaluatorType === "Client" ? "Cancel" : "Cancelar",
    });
  };

  const ModalEvaluationCancel = () => {
    setModalInfo({
      type: "confirm",
      title:
        evaluatorType === "Client" ? "Exit evaluation" : "Sair da avaliação",
      content: (
        <p style={{ margin: "0" }}>
          {evaluatorType === "Client"
            ? "You can continue later, your progress was saved automatically."
            : "Podes continuar mais tarde, o teu progresso foi guardado automaticamente."}
        </p>
      ),
      action: () => {
        navigate("/");
      },
      closeAction: () => setModalInfo(null),
      confirmActionLabel:
        evaluatorType === "Client" ? "Yes, exit" : "Sim, sair",
      closeActionLabel:
        evaluatorType === "Client" ? "No, continue" : "Não, continuar",
    });
  };

  return (
    <div className={`page-container`}>
      {data.length >= 1 ? (
        <div className={styles.container}>
          <Categories
            data={data.map((category) => category.categoryName)}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            progressActiveCategory={getProgressActiveCategory()}
            completedCategories={computedCompletedCategories}
          />

          <div className={styles.content}>
            {!autoCompleted && !hideWarning && (
              <div className={styles.warningBox}>
                <div className={styles.warningContent}>
                  <img
                    src={exclamationIcon}
                    alt="Warning Icon"
                    className={styles.warningIcon}
                  />
                  <span className={styles.warningText}>
                    {evaluatorType === "Client"
                      ? "To complete the evaluation, all questions must be answered."
                      : "Para concluíres a avaliação, todas as perguntas devem ser respondidas."}
                  </span>
                </div>
                <button
                  className={styles.warningClose}
                  onClick={() => setHideWarning(true)}
                >
                  <img src={CloseIcon} alt="Fechar" />
                </button>
              </div>
            )}
            <ProgressBar
              progress={getProgressActiveCategory()}
              categoryName={activeCategory}
              numberOfQuestions={
                data.find(
                  (category) => category.categoryName === activeCategory
                )?.questions.length || 0
              }
              evaluatorType={evaluatorType}
              autoCompleted={autoCompleted}
            />

            {data
              .find((category) => category.categoryName === activeCategory)
              ?.questions.map((question, index) => {
                switch (question.questionType) {
                  case "Single Choice":
                  case "Boolean":
                    return (
                      <div
                        key={question.id}
                        className={styles.questionContainer}
                      >
                        <p className={styles.questionLabel}>
                          {index + 1}- {question.name}
                        </p>
                        <Choice
                          key={question.id}
                          question={question}
                          value={responses?.[question.id] || ""}
                          onChange={(value) =>
                            onResponseChange(
                              question.id,
                              value,
                              question.questionType
                            )
                          }
                          disabled={autoCompleted}
                        />
                      </div>
                    );
                  case "Text":
                    return (
                      <div
                        className={styles.questionContainer}
                        key={question.id}
                      >
                        <p className={styles.questionLabel}>
                          {index + 1}- {question.name}
                        </p>
                        <textarea
                          ref={(el) => {
                            textareaRefs.current[question.id] = el;
                            if (el) {
                              adjustTextareaHeight(el);
                            }
                          }}
                          className={styles.textInput}
                          rows={3}
                          value={responses?.[question.id] || ""}
                          onChange={(e) => {
                            adjustTextareaHeight(e.currentTarget);
                            onResponseChange(
                              question.id,
                              e.target.value,
                              "Text"
                            );
                          }}
                          onBlur={(e) => {
                            onSaveResponses(question.id, e.currentTarget.value);
                          }}
                          onInput={(e) => {
                            adjustTextareaHeight(e.currentTarget);
                          }}
                          disabled={autoCompleted}
                        />
                      </div>
                    );
                  default:
                    return null;
                }
              })}
            <div className={styles.savingContainer}>
              {savingResponses ? (
                <p className={styles.savingMessage}>
                  <svg
                    className={styles.savedIcon}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.01789 5.52054C1.03128 4.93527 1.15182 4.36741 1.37816 3.83304C1.60985 3.28393 1.942 2.79241 2.36521 2.36786C2.78843 1.9433 3.28128 1.61116 3.83039 1.37946C4.39825 1.13973 5.00093 1.01786 5.62369 1.01786C6.24646 1.01786 6.84914 1.13973 7.41566 1.37946C7.96319 1.61069 8.46041 1.94641 8.8795 2.36786C9.01209 2.50045 9.13664 2.64107 9.25182 2.78839L8.44557 3.41786C8.42962 3.4302 8.41747 3.44679 8.41053 3.46573C8.40359 3.48466 8.40213 3.50517 8.40633 3.5249C8.41053 3.54463 8.42021 3.56277 8.43426 3.57724C8.44831 3.59171 8.46615 3.60192 8.48575 3.6067L10.8389 4.18259C10.9058 4.19866 10.9715 4.14777 10.9715 4.07946L10.9822 1.6567C10.9822 1.56696 10.8791 1.51607 10.8094 1.57232L10.0541 2.16295C9.02414 0.845089 7.42236 0 5.62236 0C2.55405 0 0.0576228 2.45893 3.34902e-05 5.51518C-0.000323972 5.52947 0.00218321 5.54369 0.00740737 5.557C0.0126315 5.57031 0.0204669 5.58244 0.0304519 5.59268C0.0404368 5.60291 0.0523694 5.61105 0.0655463 5.6166C0.0787232 5.62215 0.0928779 5.625 0.107176 5.625H0.910748C0.969676 5.625 1.01655 5.57813 1.01789 5.52054ZM11.1429 5.625H10.3393C10.2804 5.625 10.2335 5.67188 10.2322 5.72946C10.2188 6.31473 10.0982 6.88259 9.87191 7.41696C9.64021 7.96607 9.30807 8.45893 8.88486 8.88214C8.45769 9.31107 7.94983 9.6512 7.39058 9.8829C6.83132 10.1146 6.23173 10.2333 5.62637 10.2321C5.02122 10.2333 4.42184 10.1146 3.8628 9.88286C3.30377 9.65115 2.79615 9.31104 2.36923 8.88214C2.23664 8.74955 2.11209 8.60893 1.99691 8.46161L2.80316 7.83214C2.81911 7.8198 2.83126 7.80321 2.8382 7.78427C2.84514 7.76534 2.8466 7.74483 2.8424 7.7251C2.8382 7.70537 2.82852 7.68723 2.81447 7.67276C2.80042 7.65829 2.78257 7.64808 2.76298 7.6433L0.409855 7.06741C0.342891 7.05134 0.277266 7.10223 0.277266 7.17054L0.267891 9.59464C0.267891 9.68438 0.371016 9.73527 0.440659 9.67902L1.19602 9.08839C2.22593 10.4049 3.82771 11.25 5.62771 11.25C8.69736 11.25 11.1924 8.78973 11.25 5.73482C11.2504 5.72053 11.2479 5.70631 11.2427 5.693C11.2374 5.67969 11.2296 5.66756 11.2196 5.65732C11.2096 5.64709 11.1977 5.63896 11.1845 5.6334C11.1713 5.62785 11.1572 5.625 11.1429 5.625V5.625Z"
                      fill="rgb(111, 111, 118)"
                    />
                  </svg>
                  {evaluatorType === "Client"
                    ? "Saving as draft..."
                    : "A guardar como rascunho..."}
                </p>
              ) : lastSavedTime ? (
                <p className={styles.savingMessage}>
                  <svg
                    className={styles.savedIcon}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.50443 3.87012H7.8763C7.73969 3.87012 7.60978 3.93574 7.52942 4.04824L5.42407 6.96789L4.4705 5.64467C4.39014 5.53351 4.26157 5.46655 4.12362 5.46655H3.4955C3.40844 5.46655 3.35755 5.56565 3.40844 5.63664L5.07719 7.95092C5.11661 8.00594 5.16858 8.05078 5.22879 8.08171C5.289 8.11263 5.35571 8.12877 5.4234 8.12877C5.49109 8.12877 5.5578 8.11263 5.61801 8.08171C5.67821 8.05078 5.73018 8.00594 5.7696 7.95092L8.59014 4.04021C8.64237 3.96922 8.59148 3.87012 8.50443 3.87012Z"
                      fill="#6F6F76"
                    />
                    <path
                      d="M6 0C2.68661 0 0 2.68661 0 6C0 9.31339 2.68661 12 6 12C9.31339 12 12 9.31339 12 6C12 2.68661 9.31339 0 6 0ZM6 10.9821C3.24911 10.9821 1.01786 8.75089 1.01786 6C1.01786 3.24911 3.24911 1.01786 6 1.01786C8.75089 1.01786 10.9821 3.24911 10.9821 6C10.9821 8.75089 8.75089 10.9821 6 10.9821Z"
                      fill="#6F6F76"
                    />
                  </svg>
                  {evaluatorType === "Client"
                    ? `Saved ${getRelativeTime(lastSavedTime)}`
                    : `Guardado ${getRelativeTime(lastSavedTime)}`}
                </p>
              ) : null}
              <div className={styles.buttonContainer}>
                <WhiteButton
                  text={
                    !autoCompleted && allCategoriesAnswered
                      ? evaluatorType === "Client"
                        ? "Cancel"
                        : "Cancelar"
                      : evaluatorType === "Client"
                      ? "Previous"
                      : "Anterior"
                  }
                  onClick={() => {
                    if (!autoCompleted && allCategoriesAnswered) {
                      ModalEvaluationCancel();
                    } else {
                      formerCategory();
                    }
                  }}
                  className={styles.button}
                  disabled={activeCategory === data[0].categoryName}
                />
                <BlueButton
                  text={
                    autoCompleted &&
                    activeCategory === data[data.length - 1].categoryName
                      ? evaluatorType === "Client"
                        ? "Close"
                        : "Fechar"
                      : !autoCompleted && allCategoriesAnswered
                      ? evaluatorType === "Client"
                        ? "Submit"
                        : "Submeter"
                      : evaluatorType === "Client"
                      ? "Next"
                      : "Seguinte"
                  }
                  onClick={() => {
                    if (
                      autoCompleted &&
                      activeCategory === data[data.length - 1].categoryName
                    ) {
                      navigate("/");
                    } else if (!autoCompleted && allCategoriesAnswered) {
                      ModalEvaluationSubmition(evaluatedName);
                    } else {
                      nextCategory();
                    }
                  }}
                  className={styles.button}
                  disabled={
                    autoCompleted &&
                    activeCategory === data[data.length - 1].categoryName
                      ? false
                      : !autoCompleted && allCategoriesAnswered
                      ? false
                      : activeCategory === data[data.length - 1].categoryName
                  }
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {evaluatorType === "Client"
            ? "Only one category found."
            : "Apenas uma categoria encontrada."}
        </div>
      )}
      {modalInfo && (
        <Modal
          title={modalInfo?.title!}
          type={modalInfo?.type!}
          onConfirmAction={modalInfo?.action!}
          onCloseModal={modalInfo?.closeAction!}
          confirmActionLabel={modalInfo?.confirmActionLabel!}
          closeModalLabel={modalInfo?.closeActionLabel!}
          isSending={modalInfo?.isSending!}
        >
          {modalInfo?.content!}
        </Modal>
      )}
    </div>
  );
}
